// TD-1976 — MFA recovery page (Option 1: re-enroll on a new device).
//
// Reached from the login TOTP challenge via the "Lost access to your
// authenticator?" link. The page is intentionally backend-free: when the user
// originally set up MFA, the setup screen showed them a base32 setup key
// alongside the QR code. If they saved that key, they can type it into a
// fresh authenticator app on a new device, get the rotating 6-digit codes
// back, and complete login normally. The setup key IS effectively the seed,
// not a one-time recovery code — anyone who holds it can regenerate codes.
//
// The optional validator below checks the *format* of the key locally and
// never sends it anywhere; it exists so the user can sanity-check what they
// saved before going through the trouble of typing it into their phone.
//
// ── Rollback to Option 3 (admin reset only) ────────────────────────────────
// If we ever decide we don't want users self-recovering via the setup key
// (because shared accounts make the key effectively a permanent backdoor and
// support won't notice the re-enrollment), strip everything in <main> except
// the "Email support instead" link + the "Back to sign in" link, and update
// the lede to "If you lose access to your authenticator, your workspace
// admin can reset MFA on your account. Contact support and we will help you
// regain access." No backend change required for either direction.
// ───────────────────────────────────────────────────────────────────────────

import { observer, useLocalObservable } from 'mobx-react';
import { runInAction } from 'mobx';
import { Link } from 'react-router';
import { useEffect } from 'react';
import SignLayout from '../../../components/layouts/SignLayout';
import { paths } from '../../../utils/constants/routes';
import SupportedByBlock from '../../../components/supportedByBlock';
import './index.less';

// Cognito's `AssociateSoftwareToken` returns base32-encoded TOTP secrets,
// usually 26 characters without padding (160-bit secret). RFC 4226 allows
// 128–160-bit secrets; allow a generous range here because the setup screen
// formats the key with spaces for legibility, and users may paste in either
// the canonical form or the spaced form.
const BASE32_RE = /^[A-Z2-7]+=*$/;
const MIN_LEN = 16;
const MAX_LEN = 64;

const normalizeKey = (raw) => raw.replace(/\s+/g, '').toUpperCase();

const validateSetupKey = (raw) => {
  const key = normalizeKey(raw);
  if (key.length === 0) {
    return { valid: false, message: '' };
  }
  if (key.length < MIN_LEN) {
    return { valid: false, message: `Too short — setup keys are at least ${MIN_LEN} characters.` };
  }
  if (key.length > MAX_LEN) {
    return { valid: false, message: 'Too long — looks like you pasted extra text.' };
  }
  if (!BASE32_RE.test(key)) {
    return { valid: false, message: 'Setup keys only contain letters A–Z and digits 2–7.' };
  }
  return { valid: true, message: 'Looks like a valid setup key. Add it to your authenticator app, then sign in.' };
};

const MfaRecovery = observer(() => {
  const state = useLocalObservable(() => ({
    rawKey: '',
    setRawKey(value) {
      runInAction(() => {
        state.rawKey = value;
      });
    },
    get validation() {
      return validateSetupKey(state.rawKey);
    }
  }));

  useEffect(() => {
    document.title = `transact.digital GmbH - Restore MFA access`;
  }, []);

  return (
    <SignLayout>
      <div className="mfa-recovery-wrapper">
        <h2>Restore access on a new device</h2>
        <p className="lede">
          When you set up multi-factor authentication, you saw a <b>setup key</b>{' '}
          next to the QR code. If you saved that key, you can add it to any
          authenticator app on a new device and continue signing in.
        </p>

        <ol className="recovery-steps">
          <li>
            Install an authenticator app on your new device. We support{' '}
            <b>Google Authenticator</b>, <b>Microsoft Authenticator</b>,{' '}
            <b>1Password</b>, and <b>Authy</b>.
          </li>
          <li>
            In the app, choose <b>Add account</b> → <b>Enter setup key
            manually</b> (or <b>Enter a key</b>).
          </li>
          <li>
            Paste your saved setup key. The app will start showing a new
            6-digit code every 30 seconds.
          </li>
          <li>
            Come back to{' '}
            <Link to={paths.LOGIN} className="link">
              sign in
            </Link>{' '}
            and enter the current 6-digit code.
          </li>
        </ol>

        <div className="recovery-validator">
          <div className="recovery-validator-header">
            <h3>Check your setup key (optional)</h3>
            <p className="small">
              Paste your saved setup key here to verify it&rsquo;s in the right
              format. The key never leaves your browser.
            </p>
          </div>

          <textarea
            className="recovery-key-input"
            placeholder="Paste your setup key (letters A–Z and digits 2–7)"
            value={state.rawKey}
            onChange={(e) => state.setRawKey(e.target.value)}
            rows={2}
            autoComplete="off"
            spellCheck={false}
          />

          {state.rawKey && (
            <div
              className={state.validation.valid ? 'recovery-validation-ok' : 'recovery-validation-bad'}>
              {state.validation.message}
            </div>
          )}
        </div>

        <div className="mfa-help-row">
          <a
            href="mailto:support@transact.digital?subject=MFA%20access%20lost"
            className="link help-link">
            I don&rsquo;t have my setup key — contact support
          </a>
          <Link to={paths.LOGIN} className="link help-link">
            Back to sign in
          </Link>
        </div>

        <SupportedByBlock />
      </div>
    </SignLayout>
  );
});

export default MfaRecovery;
